import React, { useState, useEffect, useCallback } from 'react';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Баг (что-то сломалось)' },
  { value: 'error', label: 'Ошибка / сбой в работе' },
  { value: 'feedback', label: 'Обратная связь' },
  { value: 'suggestion', label: 'Предложение по улучшению' },
];

const categoryLabel = (v) => CATEGORY_OPTIONS.find((o) => o.value === v)?.label || v;

const Feedback = () => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { showSuccess, showError } = useToast();
  const { isDark } = useTheme();

  const [items, setItems] = useState([]);
  const [openItems, setOpenItems] = useState([]);
  const [closedItems, setClosedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [adminNotes, setAdminNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const [openRes, closedRes] = await Promise.all([
          client.get('/feedback', { params: { status: 'open' } }),
          client.get('/feedback', { params: { status: 'closed', limit: 500 } }),
        ]);
        setOpenItems(openRes.data);
        setClosedItems(closedRes.data);
        setItems([]);
      } else {
        const { data } = await client.get('/feedback');
        setItems(data);
        setOpenItems([]);
        setClosedItems([]);
      }
    } catch (e) {
      console.error(e);
      showError('Не удалось загрузить обращения');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, showError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      showError('Введите текст обращения');
      return;
    }
    setSubmitting(true);
    try {
      await client.post('/feedback', {
        category,
        title: title.trim() || null,
        body: body.trim(),
      });
      showSuccess('Спасибо! Обращение отправлено.');
      setTitle('');
      setBody('');
      setCategory('bug');
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      let msg = 'Ошибка отправки';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d)) {
        msg = d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join(', ');
      }
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const closeItem = async (id) => {
    const comment = (adminNotes[id] || '').trim() || null;
    try {
      await client.patch(`/feedback/${id}`, {
        status: 'closed',
        admin_comment: comment,
      });
      showSuccess('Обращение закрыто');
      setAdminNotes((prev) => ({ ...prev, [id]: '' }));
      load();
    } catch (e) {
      showError('Не удалось закрыть обращение');
    }
  };

  const reopenItem = async (id) => {
    try {
      await client.patch(`/feedback/${id}`, { status: 'open' });
      showSuccess('Обращение снова открыто');
      load();
    } catch (e) {
      showError('Не удалось открыть обращение');
    }
  };

  const cardStyle = {
    background: isDark ? '#1e293b' : '#fff',
    border: `1px solid ${isDark ? 'rgba(71, 85, 105, 0.5)' : '#e2e8f0'}`,
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  };

  return (
    <div className="container-fluid" style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ marginBottom: 8, color: 'var(--text-primary)' }}>Обратная связь</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 15 }}>
        Опишите баг, ошибку или идею — администратор увидит обращение в этом же разделе и сможет отметить его
        закрытым после исправления.
      </p>

      <div style={cardStyle}>
        <h2 style={{ fontSize: 18, marginBottom: 16, color: 'var(--text-primary)' }}>Новое обращение</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Категория
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ width: '100%', maxWidth: 400, padding: 10, borderRadius: 8 }}
            >
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Тема (необязательно)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Кратко, о чём речь"
              style={{ width: '100%', padding: 10, borderRadius: 8 }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Описание
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Что произошло, на какой странице, шаги для воспроизведения…"
              rows={5}
              style={{ width: '100%', padding: 10, borderRadius: 8, resize: 'vertical' }}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Отправка…' : 'Отправить'}
          </button>
        </form>
      </div>

      {isAdmin ? (
        <>
          <div style={{ ...cardStyle, marginTop: 24 }}>
            <h2 style={{ fontSize: 18, marginBottom: 8, color: 'var(--text-primary)' }}>К обработке</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Открытые обращения. Закрытые не удаляются — они в блоке «История» ниже.
            </p>
            {loading ? (
              <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>
            ) : openItems.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>Нет открытых обращений.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {openItems.map((row) => (
                  <li
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${isDark ? 'rgba(71,85,105,0.4)' : '#e2e8f0'}`,
                      padding: '16px 0',
                    }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.12)',
                          color: isDark ? '#93c5fd' : '#1d4ed8',
                        }}
                      >
                        {categoryLabel(row.category)}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: isDark ? 'rgba(251,191,36,0.2)' : 'rgba(251,191,36,0.2)',
                          color: '#b45309',
                        }}
                      >
                        Открыто
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        #{row.id} · {row.author_username}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Создано: {new Date(row.created_at).toLocaleString('ru-RU')}
                      </span>
                    </div>
                    {row.title && (
                      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>{row.title}</div>
                    )}
                    <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                      {row.body}
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <textarea
                        placeholder="Комментарий при закрытии (что исправлено)"
                        value={adminNotes[row.id] ?? ''}
                        onChange={(e) => setAdminNotes((p) => ({ ...p, [row.id]: e.target.value }))}
                        rows={2}
                        style={{ width: '100%', maxWidth: 480, padding: 8, borderRadius: 8, marginBottom: 8 }}
                      />
                      <button type="button" className="btn btn-success btn-sm" onClick={() => closeItem(row.id)}>
                        Закрыть (исправлено)
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={{ ...cardStyle, marginTop: 24 }}>
            <h2 style={{ fontSize: 18, marginBottom: 8, color: 'var(--text-primary)' }}>История закрытых</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Сохраняются в базе; отображаются до 500 последних закрытых (по дате закрытия).
            </p>
            {loading ? (
              <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>
            ) : closedItems.length === 0 ? (
              <p style={{ color: 'var(--text-muted)' }}>Пока нет закрытых обращений.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {closedItems.map((row) => (
                  <li
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${isDark ? 'rgba(71,85,105,0.4)' : '#e2e8f0'}`,
                      padding: '16px 0',
                    }}
                  >
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.12)',
                          color: isDark ? '#93c5fd' : '#1d4ed8',
                        }}
                      >
                        {categoryLabel(row.category)}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)',
                          color: isDark ? '#86efac' : '#15803d',
                        }}
                      >
                        Закрыто
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        #{row.id} · {row.author_username}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Создано: {new Date(row.created_at).toLocaleString('ru-RU')}
                      </span>
                      {row.closed_at && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Закрыто: {new Date(row.closed_at).toLocaleString('ru-RU')}
                        </span>
                      )}
                    </div>
                    {row.title && (
                      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>{row.title}</div>
                    )}
                    <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                      {row.body}
                    </div>
                    {row.admin_comment && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          borderRadius: 8,
                          background: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9',
                          fontSize: 14,
                        }}
                      >
                        <strong style={{ color: 'var(--text-muted)' }}>Ответ администратора:</strong>
                        <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{row.admin_comment}</div>
                      </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => reopenItem(row.id)}>
                        Открыть снова
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <div style={{ ...cardStyle, marginTop: 24 }}>
          <h2 style={{ fontSize: 18, margin: 0, marginBottom: 16, color: 'var(--text-primary)' }}>Мои обращения</h2>
          {loading ? (
            <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>
          ) : items.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Пока нет записей.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {items.map((row) => (
                <li
                  key={row.id}
                  style={{
                    borderBottom: `1px solid ${isDark ? 'rgba(71,85,105,0.4)' : '#e2e8f0'}`,
                    padding: '16px 0',
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 6,
                        background: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.12)',
                        color: isDark ? '#93c5fd' : '#1d4ed8',
                      }}
                    >
                      {categoryLabel(row.category)}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 6,
                        background:
                          row.status === 'closed'
                            ? isDark
                              ? 'rgba(34,197,94,0.2)'
                              : 'rgba(34,197,94,0.15)'
                            : isDark
                              ? 'rgba(251,191,36,0.2)'
                              : 'rgba(251,191,36,0.2)',
                        color: row.status === 'closed' ? (isDark ? '#86efac' : '#15803d') : '#b45309',
                      }}
                    >
                      {row.status === 'closed' ? 'Закрыто' : 'Открыто'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      #{row.id} · {row.author_username}
                      {row.user_id === user?.id ? ' (вы)' : ''}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {new Date(row.created_at).toLocaleString('ru-RU')}
                    </span>
                    {row.status === 'closed' && row.closed_at && (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Закрыто: {new Date(row.closed_at).toLocaleString('ru-RU')}
                      </span>
                    )}
                  </div>
                  {row.title && (
                    <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>{row.title}</div>
                  )}
                  <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                    {row.body}
                  </div>
                  {row.admin_comment && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        borderRadius: 8,
                        background: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9',
                        fontSize: 14,
                      }}
                    >
                      <strong style={{ color: 'var(--text-muted)' }}>Ответ администратора:</strong>
                      <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{row.admin_comment}</div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default Feedback;
